import axios, { CancelTokenSource } from "axios";
import { useOverlayStore } from "@/stores";
import { useAdditionalAttrStore } from "@/stores/AdditionalAttrStore";
import config from "@/config.json";
import { queryParams } from "@/stores/types";
import i18n from "../lang";
import { useLoaderStore } from "@/stores/loaderState";

const httpClient = axios.create({
  timeout: config.requesttimeout * 1000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Extend AxiosRequestConfig using declaration merging
declare module "axios" {
  interface AxiosRequestConfig {
    signalKey?: string; // Custom property for AbortController management
  }
}

const abortControllers = new Map<string, AbortController>();
const cancelTokens = new Map<string, CancelTokenSource>();

// Request interceptor to attach AbortController
httpClient.interceptors.request.use((config) => {
  const { signalKey } = config;

  if (signalKey) {
    if (signalKey == "getTechnicalEvaluation") {
      useLoaderStore().setTechnicalLoading(true);
    } else {
      useLoaderStore().setSummaryLoading(true);
    }

    if (cancelTokens.has(signalKey)) {
      cancelTokens
        .get(signalKey)
        ?.cancel("Request canceled due to a new request.");
      cancelTokens.delete(signalKey);
    }

    if (abortControllers.has(signalKey)) {
      abortControllers.get(signalKey)?.abort();
    }

    const source = axios.CancelToken.source();
    cancelTokens.set(signalKey, source);
    config.cancelToken = source.token;

    // Create and attach a new AbortController
    const controller = new AbortController();
    config.signal = controller.signal;
    abortControllers.set(signalKey, controller);
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    const { signalKey } = response.config;
    if (signalKey) {
      abortControllers.delete(signalKey);
      if (signalKey == "getTechnicalEvaluation") {
        useLoaderStore().setTechnicalLoading(false);
      } else {
        useLoaderStore().setSummaryLoading(false);
      }
    }
    return response;
  },
  (error) => {
    const { signalKey } = error.config || {};
    if (signalKey) {
      abortControllers.delete(signalKey);
    }
    return Promise.reject(error);
  }
);

httpClient.interceptors.request.use(function (config) {
  const AdditionalAttrStore = useAdditionalAttrStore();
  if (AdditionalAttrStore.config.code != undefined) {
    config.params = {
      ...config.params,
      code: AdditionalAttrStore.config.code,
    };
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    if (response.data.Status === "ERROR") {
      console.log("response error", response);
      if (response.data.ErrorCode.indexOf("COMMUNICATION_ERROR") > -1) {
        return response;
      }
      const store = useOverlayStore();
      if (store.onceHiddenError === false) {
        if (response.data.ErrorText && response.data.ErrorText !== "") {
          if (response.data.ErrorCode == "#ITEMINVALID") {
            store.setError(response.data.ErrorText, false);
          } else if (response.data.ErrorCode != "#REQUESTEXECUTIONFAILED") {
            store.setError(response.data.ErrorText, true);
          } else {
            store.setError(response.data.ErrorText);
          }
        }
      } else {
        store.enableHandleError();
      }
    }
    return response;
  },
  (error) => {
    const status = error.response ? error.response.status : null;
    const store = useOverlayStore();
    const additionalAttrStore = useAdditionalAttrStore();
    const { t } = i18n.global;
    console.log("error", error);
    if (additionalAttrStore.getCommunicationConfig.externalItemNoRequestUrl) {
      return Promise.resolve(error);
    }
    if (status === 401) {
      store.setError("error.unauthorizedAccess");
    } else if (status === 404) {
      store.setError(t("error.notFound"));
    } else if (error.code == "ERR_CANCELED") {
      return Promise.resolve(error);
    } else {
      store.setError("error.other");
    }
    return Promise.reject(error);
  }
);

export const updateBaseURL = (newBaseURL: string, queryParams: queryParams) => {
  httpClient.defaults.baseURL = newBaseURL;

  if (queryParams) {
    httpClient.interceptors.request.use(function (config) {
      Object.keys(queryParams).forEach((param) => {
        config.params = config.params || {};
        // If the value is an array, convert it to a string separated by commas
        if (Array.isArray(queryParams[param])) {
          config.params[param] = queryParams[param].join(",");
        } else {
          config.params[param] = queryParams[param];
        }
      });

      return config;
    });
  }

  // httpClient.defaults.headers.common["code"] = code;
};

export default httpClient;
